/**
 * OnboardingStep4.tsx - Learning Preferences
 * Learning preferences step of the onboarding flow.
 * Collects learning skills and methods preferences.
 * Two sections: Skills and Methods.
 */
import React, { useMemo } from "react";
import { View } from "react-native";
import { useTranslation } from "react-i18next";
import OnboardingStepHeader from "./common/OnboardingStepHeader";
import OnboardingFormSection from "./common/OnboardingFormSection";
import ChipSelector, { ChipOption } from "../../common/form/ChipSelect/ChipSelector";
import { spacingStyles } from "../../../styles/design-system";

export interface OnboardingStep4Props {
  selectedSkills: string[];
  onSkillsChange: (skills: string[]) => void;
  selectedLearningMethods: string[];
  onLearningMethodsChange: (methods: string[]) => void;
}

function OnboardingStep4({
  selectedSkills,
  onSkillsChange,
  selectedLearningMethods,
  onLearningMethodsChange,
}: OnboardingStep4Props) {
  const { t } = useTranslation();
  const skillOptions: ChipOption[] = useMemo(
    () => [
      { key: "problem_solving", value: t("text.companion_onboarding_skill_problem_solving") },
      { key: "exam_prep", value: t("text.companion_onboarding_skill_exam_prep") },
      { key: "writing", value: t("text.companion_onboarding_skill_writing") },
      { key: "coding", value: t("text.companion_onboarding_skill_coding") },
      { key: "memorization", value: t("text.companion_onboarding_skill_memorization") },
      { key: "time_management", value: t("text.companion_onboarding_skill_time_management") },
    ],
    [t]
  );
  const learningMethodOptions: ChipOption[] = useMemo(
    () => [
      { key: "practice_problems", value: t("text.companion_onboarding_method_practice") },
      { key: "step_by_step", value: t("text.companion_onboarding_method_step_by_step") },
      { key: "visual_examples", value: t("text.companion_onboarding_method_visual") },
      { key: "quick_summaries", value: t("text.companion_onboarding_method_summaries") },
      { key: "reading", value: t("text.companion_onboarding_method_reading") },
      { key: "hands_on", value: t("text.companion_onboarding_method_hands_on") },
    ],
    [t]
  );

  return (
    <View style={spacingStyles.mNone}>
      <OnboardingStepHeader
        titleKey="companion_onboarding_step4_title"
        subtitleKey="companion_onboarding_step4_subtitle"
      />

      <OnboardingFormSection labelKey="companion_onboarding_step4_skills_label" isUppercase>
        <ChipSelector
          options={skillOptions}
          selectedKeys={selectedSkills}
          onSelectionChange={onSkillsChange}
          multiSelect
        />
      </OnboardingFormSection>

      <OnboardingFormSection labelKey="companion_onboarding_step4_methods_label" isUppercase>
        <ChipSelector
          options={learningMethodOptions}
          selectedKeys={selectedLearningMethods}
          onSelectionChange={onLearningMethodsChange}
          multiSelect
        />
      </OnboardingFormSection>
    </View>
  );
}

export default OnboardingStep4;
